// Achievement badge catalog + evaluator.
// Badges are computed from the user's completions/notes/streak/profile data
// and persisted to `user_achievements` so we know which one was *just* earned
// (for "you unlocked X" celebrations) and so future leaderboards work.

import type { Completion } from './store';
import type { Domain, AgeBand, Milestone } from './types';
import { DOMAIN_META } from './types';

export type BadgeCategory = 'milestone' | 'streak' | 'domain' | 'secret' | 'pioneer';
export type BadgeRarity = 'common' | 'rare' | 'epic' | 'legendary';

export interface Badge {
  id: string;
  emoji: string;
  title: string;
  description: string;       // shown on the locked card ("Complete your first milestone")
  flavor: string;            // shown on the earned card ("Every journey begins with one tiny step.")
  category: BadgeCategory;
  rarity: BadgeRarity;
  /** HSL trio used for the badge's gradient frame */
  hsl: string;
  hidden?: boolean;          // secret badges show as "???" until earned
}

export const BADGES: Badge[] = [
  // ── Milestone-based ──────────────────────────────────────────────
  { id: 'first-steps',     emoji: '🌱', title: 'First Steps',  description: 'Log your first milestone',                  flavor: 'Every journey begins with one tiny step.', category: 'milestone', rarity: 'common',    hsl: '142 60% 55%' },
  { id: 'in-bloom',        emoji: '🌸', title: 'In Bloom',     description: 'Log 5 milestones',                          flavor: 'Five little moments, all yours to keep.',  category: 'milestone', rarity: 'common',    hsl: '340 75% 70%' },
  { id: 'rooted',          emoji: '🌳', title: 'Rooted',       description: 'Log 15 milestones',                         flavor: 'Strong roots, slow magic.',                category: 'milestone', rarity: 'rare',      hsl: '120 35% 45%' },
  { id: 'constellation-m', emoji: '🌟', title: 'Constellation',description: 'Log 30 milestones',                         flavor: 'Every memory is a star in their sky.',     category: 'milestone', rarity: 'epic',      hsl: '48 95% 60%' },

  // ── Streak-based ────────────────────────────────────────────────
  { id: 'sparked',   emoji: '🔥', title: 'Sparked',  description: 'Reach a 3-day streak',  flavor: 'Showing up is the whole game.',          category: 'streak', rarity: 'common',    hsl: '20 90% 60%' },
  { id: 'kindled',   emoji: '🔥', title: 'Kindled',  description: 'Reach a 7-day streak',  flavor: 'A whole week of paying attention.',      category: 'streak', rarity: 'rare',      hsl: '14 85% 55%' },
  { id: 'daybreak',  emoji: '🌅', title: 'Daybreak', description: 'Reach a 30-day streak', flavor: 'A month of dawn-fresh attention.',       category: 'streak', rarity: 'epic',      hsl: '32 90% 60%' },
  { id: 'celestial', emoji: '🌌', title: 'Celestial',description: 'Reach a 100-day streak',flavor: 'Rare air. You are extraordinary at this.', category: 'streak', rarity: 'legendary', hsl: '260 70% 60%' },

  // ── Domain-based ────────────────────────────────────────────────
  { id: 'wordsmith',  emoji: '🗣️', title: "Wordsmith's Garden", description: 'Complete every Language milestone in any age band', flavor: 'Words are how love travels.',                category: 'domain', rarity: 'rare', hsl: 'var(--domain-language)' },
  { id: 'social-fly', emoji: '🦋', title: 'Social Butterfly',    description: 'Complete every Social milestone in any age band',   flavor: 'They are learning how to be with people.',    category: 'domain', rarity: 'rare', hsl: 'var(--domain-social)' },
  { id: 'curious',    emoji: '🧠', title: 'Curious Mind',        description: 'Complete every Cognitive milestone in any age band',flavor: 'Wonder is their superpower.',                  category: 'domain', rarity: 'rare', hsl: 'var(--domain-cognitive)' },

  // ── Secret / surprise ───────────────────────────────────────────
  { id: 'night-owl',     emoji: '🌙', title: 'Night Owl',     description: 'Log a milestone after midnight',         flavor: 'Quiet hours, big noticing.',           category: 'secret', rarity: 'rare', hsl: '240 50% 55%', hidden: true },
  { id: 'early-bird',    emoji: '🌅', title: 'Early Bird',    description: 'Log a milestone before 6am',             flavor: 'Caught the world before anyone else.', category: 'secret', rarity: 'rare', hsl: '38 90% 60%',  hidden: true },
  { id: 'day-one',       emoji: '💝', title: 'Day One',       description: 'Use Pebble on your child\'s birthday',   flavor: 'You showed up for the day.',           category: 'secret', rarity: 'epic', hsl: '350 80% 65%', hidden: true },
  { id: 'memory-keeper', emoji: '📔', title: 'Memory Keeper', description: 'Add 10 notes to milestones',             flavor: 'A love letter, written one line at a time.', category: 'secret', rarity: 'rare', hsl: '20 50% 50%',  hidden: true },
  { id: 'year-one',      emoji: '🎂', title: 'Year One',      description: 'Use Pebble for a full year',             flavor: 'A year of noticing the small things.', category: 'secret', rarity: 'legendary', hsl: '300 70% 60%', hidden: true },

  // ── Pioneer (auto-granted at first eval for any current user) ───
  { id: 'pioneer', emoji: '🏅', title: 'Pioneer', description: 'One of the first 100 families to test Pebble', flavor: 'You helped shape what this is.', category: 'pioneer', rarity: 'legendary', hsl: '45 90% 55%' },
];

export const RARITY_META: Record<BadgeRarity, { label: string; ringHsl: string }> = {
  common:    { label: 'Common',    ringHsl: '210 15% 60%' },
  rare:      { label: 'Rare',      ringHsl: '210 80% 60%' },
  epic:      { label: 'Epic',      ringHsl: '280 70% 60%' },
  legendary: { label: 'Legendary', ringHsl: '38 95% 55%' },
};

export interface EvalContext {
  completions: Completion[];
  milestones: Milestone[];
  bands: AgeBand[];
  notesCount: number;
  streakDays: number;
  childBirthdate?: string | null;
  accountCreatedAt?: string | null;
}

/** Returns the set of badge IDs the user currently qualifies for. */
export function evaluateBadges(ctx: EvalContext): Set<string> {
  const earned = new Set<string>();
  const n = ctx.completions.length;

  // Milestone counts
  if (n >= 1)  earned.add('first-steps');
  if (n >= 5)  earned.add('in-bloom');
  if (n >= 15) earned.add('rooted');
  if (n >= 30) earned.add('constellation-m');

  // Streak
  if (ctx.streakDays >= 3)   earned.add('sparked');
  if (ctx.streakDays >= 7)   earned.add('kindled');
  if (ctx.streakDays >= 30)  earned.add('daybreak');
  if (ctx.streakDays >= 100) earned.add('celestial');

  // Domain — completed ALL of a domain in any band
  const completedIds = new Set(ctx.completions.map(c => c.milestoneId));
  const domainBadgeMap: Record<string, Domain> = {
    'wordsmith': 'language',
    'social-fly': 'social',
    'curious': 'cognitive',
  };
  for (const [badgeId, domain] of Object.entries(domainBadgeMap)) {
    const wonAnyBand = ctx.bands.some(b => {
      const inDomain = b.milestones.filter(m => m.domain === domain);
      return inDomain.length > 0 && inDomain.every(m => completedIds.has(m.id));
    });
    if (wonAnyBand) earned.add(badgeId);
  }

  // Secret — time-of-day on any completion
  for (const c of ctx.completions) {
    const h = new Date(c.completedAt).getHours();
    if (h < 6) earned.add('early-bird');
    if (h === 0 || h >= 1 && h < 5) {
      // strictly "after midnight" feel: 0:00–4:59
      earned.add('night-owl');
    }
  }

  // Day One — logged on child's birthday (any year)
  if (ctx.childBirthdate) {
    const bd = new Date(ctx.childBirthdate);
    const bdMonth = bd.getMonth();
    const bdDay = bd.getDate();
    if (ctx.completions.some(c => {
      const d = new Date(c.completedAt);
      return d.getMonth() === bdMonth && d.getDate() === bdDay;
    })) {
      earned.add('day-one');
    }
  }

  // Memory keeper
  if (ctx.notesCount >= 10) earned.add('memory-keeper');

  // Year one
  if (ctx.accountCreatedAt) {
    const ageMs = Date.now() - new Date(ctx.accountCreatedAt).getTime();
    if (ageMs >= 365 * 24 * 60 * 60 * 1000) earned.add('year-one');
  }

  // Pioneer — granted to all current beta users (we'll cap at the DB level later)
  earned.add('pioneer');

  return earned;
}

/** Helper for visual category headers in the grid. */
export const CATEGORY_META: Record<BadgeCategory, { label: string; emoji: string }> = {
  milestone: { label: 'Milestones',  emoji: '🌱' },
  streak:    { label: 'Streaks',     emoji: '🔥' },
  domain:    { label: 'Domain mastery', emoji: '🧠' },
  secret:    { label: 'Hidden gems', emoji: '🌙' },
  pioneer:   { label: 'Pioneer',     emoji: '🏅' },
};

// Re-export so badge UI can show domain emojis when needed
export { DOMAIN_META };
